import { Stack } from '@mui/material';

import { AdminViewsFund } from '@/api/admin/types';
import { EntityApprovalStatus } from '@/api/entity/types';
import {
  FundApprovalStatus,
  fundApprovalStatus,
  InvestmentAvailabilityStatusType,
} from '@/api/fund/types';
import {
  BadgeColor,
  BadgeGroupCell,
  TableColumn,
  TextCell,
  TIME_CELL_MIN_WIDTH,
  TimeCell,
} from '@/design-system';
import { PagePaths } from '@/routes/page-paths';
import { SuspendedFundAlert } from '@/shared/components/suspended-fund-alert';
import { buildBlockedTooltip } from '@/utils/date';
import { formatIntOrMaxTwo, formatMoney } from '@/utils/numbers/formatters';

import { FundAvailabilityCell } from './components/fund-availability-cell';
import { FundNameCell } from './components/fund-name-cell';
import { FundsTabTableActions } from './components/funds-tab-table-actions';

export type FundsTabTableRow = {
  key: string;
  href: string;
  id: string;
  name: string;
  entityId: string;
  entityName: string;
  thumbnailUrl: string | null;
  investorsCount: number;
  tvl: number;
  investmentAvailabilityStatus: InvestmentAvailabilityStatusType | null;
  status: FundApprovalStatus;
  lastUpdated: string;
  createdAt: string;
  isEntityApproved: boolean;
  entityApprovalStatus: EntityApprovalStatus | null;
  currency: string | null;
  minInvestment: number | null;
  legalStructure: string | null;
  projectedYield: number | null;
  category: string | null;
  assets: string[] | null;
  strategies: string[] | null;
  maxFundCapacity: number | null;
  fundInceptionDate: string | null;
  navStrikeFrequency: string | null;
  lockupPeriod: string | null;
  redemptionFrequency: string | null;
  reportingFrequency: string | null;
  transactionsCount: number;
  blockedAt?: string | null;
  blockReason?: string | null;
};

export const buildTableRows = (items: AdminViewsFund[]): FundsTabTableRow[] =>
  items.map((f) => ({
    key: f.id,
    id: f.id,
    href: PagePaths.overviewFund.replace(':id', f.id),
    name: f.name,
    entityId: f.entityId,
    entityName: f.entityName,
    thumbnailUrl: f.cardImageUrl,
    investorsCount: f.investorsCount,
    tvl: f.tvl,
    investmentAvailabilityStatus: f.investmentAvailabilityStatus,
    status: f.status,
    lastUpdated: f.lastUpdated,
    createdAt: f.createdAt,
    isEntityApproved: f.isEntityApproved,
    entityApprovalStatus: f.entityApprovalStatus,
    currency: f.currency,
    minInvestment: f.minInvestment,
    legalStructure: f.legalStructure,
    projectedYield: f.projectedYield,
    category: f.category,
    assets: f.assets,
    strategies: f.strategies,
    maxFundCapacity: f.maxFundCapacity,
    fundInceptionDate: f.fundInceptionDate,
    navStrikeFrequency: f.navStrikeFrequency,
    lockupPeriod: f.lockupPeriod,
    redemptionFrequency: f.redemptionFrequency,
    reportingFrequency: f.reportingFrequency,
    transactionsCount: f.transactionsCount,
    blockedAt: f.blockedAt,
    blockReason: f.blockReason,
  }));

const fundStatusBadge: Record<
  FundApprovalStatus,
  { label: string; color?: BadgeColor; tooltip?: string }
> = {
  [fundApprovalStatus.Draft]: {
    label: 'Draft',
    color: 'default',
    tooltip: 'This is a draft which has not yet been submitted for approval.',
  },
  [fundApprovalStatus.Verified]: {
    label: 'Verified',
    color: 'primary',
    tooltip: 'This Fund has been approved by Gamma Prime.',
  },
  [fundApprovalStatus.ReVerificationPending]: {
    label: 'Re-verification Pending',
    color: 'orange',
    tooltip:
      'This Fund was previously verified and is waiting for a new approval from Gamma Prime after key information was edited.',
  },
  [fundApprovalStatus.AwaitingApproval]: {
    label: 'Awaiting Approval',
    color: 'yellow',
    tooltip: 'This Fund has been newly created and is waiting for approval from Gamma Prime.',
  },
  [fundApprovalStatus.Rejected]: {
    label: 'Rejected',
    color: 'destructive',
    tooltip: 'This Fund was reviewed and rejected by Gamma Prime.',
  },
  [fundApprovalStatus.Blocked]: {
    label: 'Blocked',
    color: 'destructive',
    tooltip: 'This verified Fund has been blocked by Gamma Prime.',
  },
};

export const fundsTableColumns: TableColumn<FundsTabTableRow>[] = [
  {
    key: 'name',
    header: 'Fund Name',
    width: '20fr',
    minWidth: 180,
    maxWidth: 320,
    sortable: true,
    cell: (row) => <FundNameCell name={row.name} thumbnailUrl={row.thumbnailUrl} />,
  },
  {
    key: 'entityName',
    header: 'Legal Entity',
    width: '13fr',
    cell: (row) => (
      <TextCell
        title={row.entityName}
        tooltip={row.entityName.length > 20 ? row.entityName : undefined}
      />
    ),
  },
  {
    key: 'investorsCount',
    header: '# Investors',
    width: '9fr',
    sortable: true,
    cell: (row) => <TextCell title={String(row.investorsCount)} />,
  },
  {
    key: 'tvl',
    header: 'TVL ($)',
    width: '10fr',
    sortable: true,
    cell: (row) => (
      <TextCell title={row.tvl ? `$${formatIntOrMaxTwo(row.tvl / 1_000_000)}M` : '$0'} />
    ),
  },
  {
    key: 'investmentAvailabilityStatus',
    header: 'Availability',
    width: '11fr',
    sortable: true,
    cell: (row) => (
      <FundAvailabilityCell investmentAvailabilityStatus={row.investmentAvailabilityStatus} />
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '14fr',
    sortable: true,
    cell: (row) => {
      const badge = fundStatusBadge[row.status];
      const finalBadge =
        row.status === fundApprovalStatus.Blocked
          ? { ...badge, tooltip: buildBlockedTooltip('Fund', row.blockedAt, row.blockReason) }
          : badge;
      return (
        <Stack direction="row" alignItems="center" gap="8px">
          <BadgeGroupCell badges={[finalBadge]} />
          <SuspendedFundAlert
            fundApprovalStatus={row.status}
            entityApprovalStatus={row.entityApprovalStatus}
          />
        </Stack>
      );
    },
  },
  {
    key: 'updatedAt',
    header: 'Last Updated',
    width: '14fr',
    minWidth: TIME_CELL_MIN_WIDTH,
    sortable: true,
    cell: (row) => <TimeCell date={row.lastUpdated} />,
  },
  {
    key: 'createdAt',
    header: 'Created Date',
    width: '14fr',
    minWidth: TIME_CELL_MIN_WIDTH,
    sortable: true,
    cell: (row) => <TimeCell date={row.createdAt} />,
  },
  {
    key: 'transactionsCount',
    header: 'Transactions',
    width: '6fr',
    sortable: true,
    cell: (row) => <TextCell title={String(row.transactionsCount)} />,
  },
  {
    key: 'currency',
    header: 'Currency',
    width: '6fr',
    sortable: true,
    cell: (row) => <TextCell title={row.currency || '—'} />,
  },
  {
    key: 'minInvestment',
    header: 'Min Investment',
    width: '10fr',
    sortable: true,
    cell: (row) => (
      <TextCell
        title={row.minInvestment != null ? formatMoney(row.minInvestment, row.currency) : '—'}
      />
    ),
  },
  {
    key: 'legalStructure',
    header: 'Fund Structure',
    width: '11fr',
    sortable: true,
    cell: (row) => <TextCell title={row.legalStructure || '—'} />,
  },
  {
    key: 'projectedYield',
    header: 'Projected Yield',
    width: '10fr',
    sortable: true,
    cell: (row) => (
      <TextCell
        title={row.projectedYield != null ? `${formatIntOrMaxTwo(row.projectedYield)}%` : '—'}
      />
    ),
  },
  {
    key: 'category',
    header: 'Fund Category',
    width: '11fr',
    sortable: true,
    cell: (row) => <TextCell title={row.category || '—'} />,
  },
  {
    key: 'assets',
    header: 'Asset',
    width: '11fr',
    cell: (row) => (
      <TextCell
        title={row.assets?.join(', ') || '—'}
        tooltip={row.assets ? row.assets.join(', ') : undefined}
      />
    ),
  },
  {
    key: 'strategies',
    header: 'Strategy',
    width: '11fr',
    cell: (row) => (
      <TextCell
        title={row.strategies?.join(', ') || '—'}
        tooltip={row.strategies ? row.strategies.join(', ') : undefined}
      />
    ),
  },
  {
    key: 'maxFundCapacity',
    header: 'Max Fund Capacity',
    width: '10fr',
    sortable: true,
    cell: (row) => (
      <TextCell
        title={row.maxFundCapacity != null ? `$${formatIntOrMaxTwo(row.maxFundCapacity)}M` : '—'}
      />
    ),
  },
  {
    key: 'fundInceptionDate',
    header: 'Fund Inception Date',
    width: '14fr',
    minWidth: TIME_CELL_MIN_WIDTH,
    cell: (row) =>
      row.fundInceptionDate ? <TimeCell date={row.fundInceptionDate} /> : <TextCell title="—" />,
  },
  {
    key: 'navStrikeFrequency',
    header: 'NAV Strike Frequency',
    width: '10fr',
    cell: (row) => <TextCell title={row.navStrikeFrequency || '—'} />,
  },
  {
    key: 'lockupPeriod',
    header: 'Lockup',
    width: '8fr',
    sortable: true,
    cell: (row) => <TextCell title={row.lockupPeriod || '—'} />,
  },
  {
    key: 'redemptionFrequency',
    header: 'Redemption Frequency',
    width: '8fr',
    sortable: true,
    cell: (row) => <TextCell title={row.redemptionFrequency || '—'} />,
  },
  {
    key: 'reportingFrequency',
    header: 'Reporting Frequency',
    width: '8fr',
    sortable: true,
    cell: (row) => <TextCell title={row.reportingFrequency || '—'} />,
  },
  {
    key: 'actions',
    header: 'Actions',
    width: '104px',
    align: 'right',
    pinned: true,
    cell: (row) => (
      <FundsTabTableActions
        id={row.id}
        name={row.name}
        status={row.status}
        entityId={row.entityId}
      />
    ),
  },
];
